import type { CardTokenParams } from "~/modules/mercadopago/module";
import formatDate from "~/utils/format-date";
import { getPriceLabel } from "~/utils/subscription";

export const formatCreditCardInfo = (item: IResponseCreditCardInfo): IModelCreditCard => {
  const cardExpiration = item.expireMonth && item.expireYear ? `${item.expireMonth}/${String(item.expireYear).substring(2)}` : "";

  return {
    id: item.id ?? "",
    cardHolderName: item.displayName ?? "",
    cardNumber: item.number ?? "",
    cardExpiration,
    cardCVV: "",
    cardType: item.type,
  };
};

export const formatMercadoPagoCard = (card: IModelCreditCard): CardTokenParams => {
  const [month, year] = card.cardExpiration.split("/");

  return {
    cardNumber: card.cardNumber.replace(/\s+/g, ""),
    cardholderName: card.cardHolderName,
    cardExpirationMonth: month,
    cardExpirationYear: year,
    securityCode: card.cardCVV,
  };
};

export const formatRedeemAccessCode = (item: IResponseReedemCode): IModelCommerce => ({
  id: item._id,
  title: item.title,
  description: item.description,
  price: item.price,
  currency: item.currency || "N/A",
  startDate: item.startDate,
  endDate: item.endDate,
  isActive: item.active,
  autoRenew: item.autoRenew,
  type: item.type,
  originalProductId: item.originalProductId,
  lastPayment: item.lastPayment || 0,
});

export const formatOrderItem = (item: IResponseBillingHistory): IModelBillingOrder => {
  const getFormattedDate = (date: Date) => formatDate(date, "d.L.yyyy");

  const amount = getPriceLabel(item.price, item.currency);

  return {
    id: item.details.orderId,
    date: getFormattedDate(item.date),
    name: item.title,
    type: item.details.productType,
    amount,
  };
};

export const formatPaginatedOrders = (
  response: [IPaginatedResponse<IResponsePPVTicket>, IPaginatedResponse<IResponseCustomerSubscription>],
  page = 0,
  pageSize = 10,
): IPaginatedResponse<IModelBillingOrder> => {
  const paginate = (array: IResponsePPVTicket[] | IResponseCustomerSubscription[], pageSize: number, pageNumber: number) => {
    return array.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  };

  const content = response
    .reduce((acc: any, item: any) => {
      acc.entries = [...item.content.entries].concat(acc.entries ? [...acc.entries] : []);

      return acc;
    }, {});

  const filteredEntries = content.entries
    .sort((a: IResponseBillingOrder, b: IResponseBillingOrder) => new Date(b.updated).getTime() - new Date(a.updated).getTime());

  content.count = filteredEntries.length;
  content.entryCount = pageSize;
  content.itemsPerPage = pageSize;
  content.entries = paginate(filteredEntries.map(formatOrderItem), pageSize, page + 1);

  return { content, error: false, message: "ok" };
};
